import React, { useEffect, useState } from 'react'
import {
  Battery50Icon, BoltIcon, CpuChipIcon, ExclamationCircleIcon, ExclamationTriangleIcon, QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline'
import { checkModelsCompatibility } from '../../api/compatibility'
import WyborSprzetu from '../oferta/WyborSprzetu'
import { opisZgodnosci } from '../../lib/rynek'
import { MAKS_UWAG_SPRZETU, PUSTY_SPRZET } from '../../lib/sprzetKlienta'
import Ikona from './IkonaZlecenia'
import './Zlecenie.css'

/**
 * Sprzęt, który klient już wybrał: karty Moduły, Falownik, Magazyn energii (producent, zawężenie, model z katalogu
 * i „Kilka słów”). Wspólne dla formularza zlecenia („Wiem, jaki sprzęt chcę”) i prostego doboru („Mam już wybrany
 * sprzęt”, decyzja właściciela 2026-10-26), żeby oba miejsca zapisywały sprzęt do opisu zlecenia tak samo.
 *
 * Stan i zamiana na opis zlecenia: `lib/sprzetKlienta.js` (`PUSTY_SPRZET`, `opisSprzetuKlienta`).
 */

export { MAKS_UWAG_SPRZETU, PUSTY_SPRZET, elementSprzetu, opisSprzetuKlienta, maOpisSprzetu } from '../../lib/sprzetKlienta'

/**
 * Karta jednego elementu (uwagi przeglądu I1): ramka i nagłówek z ikoną jak w kafelkach sprzętu u wykonawcy
 * (`SprzetZlecenia`), żeby od razu było widać, gdzie zaczyna się falownik, a gdzie magazyn.
 */
export const KartaSprzetu = ({ Ikona: IkonaKarty, tytul, children }) => (
  <section aria-label={tytul} className="karta-sprzetu">
    <h3 className="karta-sprzetu-glowa">
      <span className="karta-sprzetu-ikona" aria-hidden="true"><IkonaKarty className="h-5 w-5" /></span>{tytul}
    </h3>
    {children}
  </section>
)

const TONY_ZGODNOSCI = {
  ok: { Ikon: () => <Ikona nazwa="ok" rozmiar={20} />, kolor: 'text-emerald-700' },
  uwaga: { Ikon: () => <ExclamationTriangleIcon className="h-5 w-5" aria-hidden="true" />, kolor: 'text-amber-700' },
  blad: { Ikon: () => <ExclamationCircleIcon className="h-5 w-5" aria-hidden="true" />, kolor: 'text-red-700' },
  brak: { Ikon: () => <QuestionMarkCircleIcon className="h-5 w-5" aria-hidden="true" />, kolor: 'text-gray-600' },
}

/**
 * Zgodność falownika z magazynem przy wyborze modeli wprost (D4), z tej samej macierzy co `/kompatybilnosc`.
 * Sprawdzamy tylko konkretne modele; samo zawężenie (producent, seria) dostaje podpowiedź, jak to sprawdzić.
 */
export const ZgodnoscPary = ({ falownik, magazyn }) => {
  const idFalownika = falownik?.model?.id
  const idMagazynu = magazyn?.model?.id
  const [stan, setStan] = useState({ para: null, wynik: null })

  useEffect(() => {
    if (!idFalownika || !idMagazynu) return undefined
    let anulowane = false
    const para = `${idFalownika}:${idMagazynu}`
    setStan({ para, wynik: 'laduje' })
    checkModelsCompatibility(idFalownika, idMagazynu)
      .then((w) => { if (!anulowane) setStan({ para, wynik: w }) })
      .catch(() => { if (!anulowane) setStan({ para, wynik: null }) })
    return () => { anulowane = true }
  }, [idFalownika, idMagazynu])

  if (!falownik || !magazyn) return null
  if (!idFalownika || !idMagazynu) {
    return <p className="pole-podpowiedz">Wybierz konkretne modele falownika i magazynu, a sprawdzimy, czy do siebie pasują.</p>
  }
  if (stan.wynik === 'laduje') return <p className="pole-podpowiedz" aria-live="polite">Sprawdzamy, czy falownik pasuje do magazynu…</p>
  const opis = opisZgodnosci(stan.wynik)
  if (!opis) return null
  const { Ikon, kolor } = TONY_ZGODNOSCI[opis.ton]
  return (
    <div aria-live="polite" className="flex gap-2.5 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3">
      <span className={`mt-0.5 flex-shrink-0 ${kolor}`}><Ikon /></span>
      <div className="min-w-0 text-[13.5px] leading-snug">
        <p className={`font-semibold ${kolor}`}>{opis.tytul}</p>
        <p className="mt-0.5 text-gray-600">
          {opis.opis}
          {stan.wynik?.pair_page_url && (
            <>{' '}<a href={stan.wynik.pair_page_url} target="_blank" rel="noopener noreferrer" className="font-semibold">Szczegóły pary</a></>
          )}
        </p>
      </div>
    </div>
  )
}

/**
 * Karty sprzętu. `wartosc` jak `PUSTY_SPRZET`, `onZmiana(nowyStan)`. `blad`: tekst błędu pod kartami.
 * `bezOpisu`: bez akapitu nad kartami. `wlasnyZakres`: poza formularzem zlecenia (prosty dobór) karty same niosą klasę `vv-dom` z tokenami kolorów.
 */
const SprzetKlienta = ({ wartosc, onZmiana, blad = null, wlasnyZakres = false, bezOpisu = false }) => {
  const s = { ...PUSTY_SPRZET, ...(wartosc || {}), uwagi: { ...PUSTY_SPRZET.uwagi, ...(wartosc?.uwagi || {}) } }
  const zmien = (pole) => (v) => onZmiana({ ...s, [pole]: v })
  const zmienUwagi = (rodzaj) => (e) => onZmiana({ ...s, uwagi: { ...s.uwagi, [rodzaj]: e.target.value } })
  const uwagi = (rodzaj, placeholder) => (
    <label className="pole" style={{ marginTop: 8 }}><span>Kilka słów <small>(opcjonalnie)</small></span>
      <input className="wejscie" maxLength={MAKS_UWAG_SPRZETU} placeholder={placeholder} value={s.uwagi[rodzaj]}
             onChange={zmienUwagi(rodzaj)} />
    </label>
  )
  return (
    <div className={`flex flex-col gap-4${wlasnyZakres ? ' vv-dom' : ''}`} style={wlasnyZakres ? { background: 'transparent' } : undefined}>
      {/* `bezOpisu`: gdy pytanie nad kartami mówi już to samo (kreator krokowy). */}
      {!bezOpisu && (
        <p className="grupa-opis" style={{ margin: 0 }}>
          Wystarczy jedna informacja, np. sam producent. Model z listy dostaną wykonawcy jako wzór, resztę jako Twoje preferencje.
        </p>
      )}
      <KartaSprzetu Ikona={BoltIcon} tytul="Moduły">
        <WyborSprzetu kategoria="panel" wartosc={s.panel} onZmiana={zmien('panel')} placeholder="np. JA Solar JAM60D41-500" etykietaMocy="Moc" />
        <label className="pole" style={{ marginTop: 8, maxWidth: 220 }}>Liczba modułów
          <input className="wejscie" inputMode="numeric" placeholder="np. 16" value={s.liczbaModulow}
                 onChange={(e) => onZmiana({ ...s, liczbaModulow: e.target.value })} />
        </label>
        {uwagi('moduly', 'np. czarne, dobrej jakości')}
      </KartaSprzetu>
      <KartaSprzetu Ikona={CpuChipIcon} tytul="Falownik">
        <WyborSprzetu kategoria="inwerter" wartosc={s.falownik} onZmiana={zmien('falownik')} placeholder="np. Deye SUN-8K-SG05LP3" etykietaMocy="Moc" />
        {uwagi('falownik', 'np. dobrej jakości, hybrydowy')}
      </KartaSprzetu>
      <KartaSprzetu Ikona={Battery50Icon} tytul="Magazyn energii">
        <WyborSprzetu kategoria="magazyn" wartosc={s.magazyn} onZmiana={zmien('magazyn')} placeholder="np. Felicity FLA48460" etykietaMocy="Pojemność" />
        {uwagi('magazyn', 'np. z możliwością rozbudowy')}
      </KartaSprzetu>
      <ZgodnoscPary falownik={s.falownik} magazyn={s.magazyn} />
      {blad}
    </div>
  )
}

export default SprzetKlienta
